import styles from './AdminTaxInvoiceInfo.module.css'
import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {GetTaxInvoiceInfoResponseData} from "../../../types/admin";
import {useAlert} from "../../common/error/AlertContext";
import {
    cancelTaxInvoiceAPI,
    deleteTaxInvoiceAPI,
    getTaxInvoiceInfoAPI,
    issueTaxInvoiceAPI
} from "../../../services/admin";
import Button from "../../common/button/Button";
import TextCard from "../../common/textCard/TextCard";
import AdminTaxInvoiceCancelPopUp from "../../../views/admin/popup/AdminTaxInvoiceCancelPopUp";
import AdminTaxInvoiceIssuePopUp from "../../../views/admin/popup/AdminTaxInvoiceIssuePopUp";
import ModifyTaxInvoicePopUp from "../../../views/admin/popup/ModifyTaxInvoicePopUp";
import CreateModifyTaxInvoicePopUp from "../../../views/admin/popup/CreateModifyTaxInvoicePopUp";

interface AdminTaxInvoiceInfoProps {
    taxInvoiceId: number;
}
const AdminTaxInvoiceInfo: React.FC<AdminTaxInvoiceInfoProps> = ({taxInvoiceId}) => {
    const {id} = useParams();
    const [data, setData] = useState<GetTaxInvoiceInfoResponseData | null>(null);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
    const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
    const [isModifyInvoiceModalOpen, setIsModifyInvoiceModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const {showAlert} = useAlert();
    const navigate = useNavigate();

    //발행 취소
    const handleCancelClick = () => {
        setIsCancelModalOpen(true);
    };

    //발행
    const handleIssueClick = () => {
        setIsIssueModalOpen(true);
    };

    //수정
    const handleModifyClick = () => {
        setIsModifyModalOpen(true);
    };

    //수정
    const handleModifyClose = () => {
        setIsModifyModalOpen(false);
        fetchData();
    };

    //수정 세금계산서 발행
    const handleModifyInvoiceClick = () => {
        setIsModifyInvoiceModalOpen(true);
    };

    //세금게산서 삭제
    const handleDelete = async () => {
        try {
            await deleteTaxInvoiceAPI(taxInvoiceId);
            navigate("/admin/billing");
            showAlert('세금계산서가 삭제되었습니다.', 'info');
        } catch (error) {
            console.error('Error fetching payment info:', error);
            showAlert('결제 정보를 불러오는데 실패했습니다.', 'error');
        }
    };

    //데이터 세팅
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getTaxInvoiceInfoAPI(taxInvoiceId);
            setData(response);
        } catch (error) {
            console.error('Error fetching payment info:', error);
            showAlert('결제 정보를 불러오는데 실패했습니다.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    //발행 취소 로직
    const handleCancelConfirm = async (memo: string) => {
        try {
            //발행 취소 API
            await cancelTaxInvoiceAPI({
                taxInvoiceId: taxInvoiceId,
                memo: memo
            })
            showAlert('발행이 취소되었습니다.', 'info'); //알림창
            setIsCancelModalOpen(false); //모달 닫기
            fetchData();
        } catch (error) {
            console.error('Error canceling payment:', error);
            showAlert('발행 취소에 실패했습니다.', 'error');
        }
    };

    //발행 로직
    const handleIssueConfirm = async (memo: string) => {
        try {
            //발행 API
            await issueTaxInvoiceAPI({
                taxInvoiceId: taxInvoiceId,
                memo: memo
            })
            showAlert('발행되었습니다.', 'info'); //알림창
            setIsIssueModalOpen(false); //모달 닫기
            fetchData();
        } catch (error) {
            console.error('Error canceling payment:', error);
            showAlert('발행에 실패했습니다.', 'error');
        }
    };

    //날짜 데이터 형식 변환
    const formatDateTime = (date: string | null) => {
        if(!date) return '-';
        return  date.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3 $4:$5:$6');
    }

    //날짜 데이터 형식 변환
    const formatDate = (date: string | null) => {
        if(!date) return '-';
        return  date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
    }

    //세금계산서 상태
    const taxInvoiceStatus = () => {
        if(!data) return '-';
        if(data?.stateCode === 100) return '임시저장';
        if(data?.stateCode === 300) return '발행완료';
        if(data?.stateCode === 301) return '전송전';
        if(data?.stateCode === 302) return '전송대기';
        if(data?.stateCode === 303) return '전송중';
        if(data?.stateCode === 304) return '전송성공';
        if(data?.stateCode === 305) return '전송실패';
        if(data?.stateCode === 600) return '발행취소';
        return '-';
    }

    //수정 사유
    const modifyReason = () => {
        if(!data) return '-';
        if(data?.modifyCode === 1) return '기재사항 착오정정';
        if(data?.modifyCode === 2) return '공급가액 변동';
        if(data?.modifyCode === 3) return '환입';
        if(data?.modifyCode === 4) return '계약 해제';
        if(data?.modifyCode === 5) return '내국신용장 사후 개설';
        if(data?.modifyCode === 6) return '착오로 인한 이중발급';
        return '-';
    }

    //클라이언트 데이터
    const getClientInfo = () => {
        if (!data) return [];
        return [
            {
                label: "회사명",
                value: data.invoiceeCorpName
            },
            {
                label: "사업자번호",
                value: data.invoiceeCorpNum
            },
            {
                label: "이메일",
                value: data.email
            },
            {
                label: "담당자 성명",
                value: data.managerName
            },
            {
                label: "담당자 번호",
                value: data?.managerPhone || "-"
            }
        ];
    };

    //세금계산서 상태 데이터
    const getInvoiceStatusInfo = () => {
        if (!data) return [];
        const items = [
            {
                label: "발행 목적",
                value: data?.purposeType || "-"
            },
            {
                label: "상태",
                value: taxInvoiceStatus()
            },
            {
                label: "상태 메모",
                value: data.stateMemo || '-'
            },
            {
                label: "상태 변경일시",
                value: formatDateTime(data?.stateDT || null)
            }
        ];
        // 수정세금계산서
        if (data.modifyCode) {
            items.push({
                label: "수정사유",
                value: modifyReason()
            });
        }
        return items
    };

    // 세금계산서 데이터
    const getInvoiceInfo = () => {
        if (!data) return [];

        return [
            {
                label: "주문명",
                value: data?.orderName || "-"
            },
            {
                label: "공급가액",
                value: `${Number(data.supplyCostTotal).toLocaleString()}원`
            },
            {
                label: "세액",
                value: `${Number(data.taxTotal).toLocaleString()}원`
            },
            {
                label: "총액",
                value: `${(Number(data.supplyCostTotal) + Number(data.taxTotal)).toLocaleString()}원`
            }
        ];
    };

    //날짜 데이터
    const getDateInfo = () => {
        if (!data) return [];

        return [
            {
                label: "작성일자",
                value: formatDate(data?.writeDate || null)
            },
            {
                label: "임시저장 일시",
                value: formatDateTime(data?.regDT || null)
            },
            {
                label: "발행일시",
                value: formatDateTime(data?.issueDT || null)
            },
            {
                label: "개봉일시",
                value: formatDateTime(data?.openDT || null)
            }
        ];
    };

    //국세청 데이터
    const getNationalTaxInfo = () => {
        if (!data) return [];

        return [
            {
                label: "국세청 승인번호",
                value: data?.ntsConfirmNum || '-'
            },
            {
                label: "국세청 전송 결과",
                value: data?.ntsResult || '-'
            },
            {
                label: "국세청 전송일시",
                value: formatDateTime(data?.ntsSendDT || null)
            },
            {
                label: "국세청 결과 수신일시",
                value: formatDateTime(data?.ntsResultDT || null)
            }
        ];
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (!data) {
        return <div className={styles.error}>세금계산서 정보를 찾을 수 없습니다.</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>세금계산서 상세 정보</h2>
                {/*임시저장 상태일 경우 발행, 수정, 삭제 버튼 출력*/}
                {data?.stateCode === 100 && (
                    <div className={styles.buttons}>
                        <Button
                            variant="primary"
                            onClick={handleIssueClick}
                            size="small"
                        >
                            발행
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleModifyClick}
                            size="small"
                        >
                            수정
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleDelete}
                            size="small"
                        >
                            삭제
                        </Button>
                    </div>
                )}
                {/*발행 상태일 경우 취소 버튼 출력*/}
                {(data?.stateCode === 300 || data?.stateCode === 301) && (
                    <Button
                        variant="secondary"
                        onClick={handleCancelClick}
                        size="small"
                    >
                        취소
                    </Button>
                )}
                {/*발행 취소 상태일 경우 삭제 버튼 출력*/}
                {data?.stateCode === 600 && (
                    <Button
                        variant="secondary"
                        onClick={handleDelete}
                        size="small"
                    >
                        삭제
                    </Button>
                )}
                {/*수정 세금계산서가 아닌 세금계산서가 전송완료 상태일 경우 수정 세금계산서 발행 버튼 출력*/}
                {(data?.stateCode === 304 && !data?.modifyCode) && (
                    <Button
                        variant="primary"
                        onClick={handleModifyInvoiceClick}
                        size="small"
                    >
                        수정 세금계산서 발행
                    </Button>
                )}
            </div>
            <div className={styles.text}>
                <div className={styles.textContainer}>
                    <TextCard items={getClientInfo()}/>
                    <TextCard items={getInvoiceStatusInfo()}/>
                    <TextCard items={getInvoiceInfo()}/>
                </div>
                <div className={styles.textContainer}>
                    <TextCard items={getDateInfo()}/>
                    <TextCard items={getNationalTaxInfo()}/>
                </div>
            </div>
            <div className={styles.textWide}>
                <TextCard items={getClientInfo()}/>
                <TextCard items={getInvoiceStatusInfo()}/>
                <TextCard items={getInvoiceInfo()}/>
                <TextCard items={getDateInfo()}/>
                <TextCard items={getNationalTaxInfo()}/>
            </div>
            <div className={styles.textNarrow}>
                <div className={styles.textContainer}>
                    <TextCard items={getClientInfo()}/>
                    <TextCard items={getInvoiceStatusInfo()}/>
                </div>
                <div className={styles.textContainer}>
                    <TextCard items={getInvoiceInfo()}/>
                    <TextCard items={getDateInfo()}/>
                </div>
                <div className={styles.textContainer}>
                    <TextCard items={getNationalTaxInfo()}/>
                </div>
            </div>

            {/*발행 취소 팝업*/}
            <AdminTaxInvoiceCancelPopUp
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelConfirm}
            />
            {/*결제 취소 팝업*/}
            <AdminTaxInvoiceIssuePopUp
                isOpen={isIssueModalOpen}
                onClose={() => setIsIssueModalOpen(false)}
                onConfirm={handleIssueConfirm}
            />
            <ModifyTaxInvoicePopUp
                isOpen={isModifyModalOpen}
                onClose={handleModifyClose}
                taxInvoiceInfo={{
                    taxInvoiceId: data.taxInvoiceId,
                    email: data.email,
                    managerName: data.managerName,
                    managerPhone: data.managerPhone,
                    supplyCost: Number(data.supplyCostTotal),
                    tax: Number(data.taxTotal)
                }}/>
            <CreateModifyTaxInvoicePopUp
                isOpen={isModifyInvoiceModalOpen}
                onClose={() => setIsModifyInvoiceModalOpen(false)}
                taxInvoiceInfo={{
                    taxInvoiceId: data.taxInvoiceId,
                    supplyCost: Number(data.supplyCostTotal),
                    tax: Number(data.taxTotal),
                    purpose: data.purposeType,
                    managerName: data.managerName,
                    managerNumber: data.managerPhone,
                    email: data.email
                }}/>
        </div>
    );
};

export default AdminTaxInvoiceInfo